import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/i18n/LanguageContext";
import { getAudioPrefs, setAudioPrefs, setAudioScope, playUiSound, type AudioProfile, type AudioVolume } from "@/lib/audioUi";

const PROFILES: { key: AudioProfile; es: string; en: string; hintEs: string; hintEn: string }[] = [
  { key: "mechanical", es: "Mecánico", en: "Mechanical", hintEs: "Tecla seca y precisa", hintEn: "Crisp precise key" },
  { key: "wood", es: "Madera", en: "Wood", hintEs: "Interruptor cálido", hintEn: "Warm switch" },
  { key: "success", es: "Éxito", en: "Success", hintEs: "Doble toque suave", hintEn: "Soft double chime" },
  { key: "alert", es: "Alerta", en: "Alert", hintEs: "Toque grave discreto", hintEn: "Low discreet tap" },
];
const VOLUMES: { key: AudioVolume; es: string; en: string }[] = [
  { key: "soft", es: "Suave", en: "Soft" },
  { key: "medium", es: "Medio", en: "Medium" },
  { key: "normal", es: "Normal", en: "Normal" },
];

/** Probador de micro-sonidos + sonido al pulsar botones cuando está activo. */
export default function AdminAudioToggle({ variant = "admin", scopeKey }: { variant?: "admin" | "panel"; scopeKey?: string }) {
  const { lang } = useLanguage();
  const en = lang === "en";
  const [prefs, setPrefs] = useState(getAudioPrefs);

  useEffect(() => {
    setAudioScope(scopeKey ?? "rl-admin-audio");
    setPrefs(getAudioPrefs());
  }, [scopeKey]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest("button, [role='tab'], [role='menuitem'], a");
      if (!el || el.closest("[data-audio-tester]")) return;
      playUiSound();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const update = (p: Parameters<typeof setAudioPrefs>[0]) => setPrefs(setAudioPrefs(p));
  const triggerClass = variant === "panel"
    ? "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground hover:bg-muted transition-colors"
    : "inline-flex h-11 w-11 items-center justify-center rounded-lg text-radar-nav-ink hover:bg-radar-nav-2 transition-colors";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={variant === "panel" ? (en ? "Panel sounds" : "Sonidos del panel") : (en ? "Back Office sounds" : "Sonidos del Back Office")}
          className={triggerClass}
        >
          {prefs.enabled ? <Volume2 className="h-5 w-5" strokeWidth={1.5} /> : <VolumeX className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-4" data-audio-tester>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{en ? "Click sounds" : "Sonido al hacer clic"}</p>
            <p className="text-xs text-muted-foreground">{en ? "Only on this browser" : "Solo en este navegador"}</p>
          </div>
          <Switch checked={prefs.enabled} onCheckedChange={(v) => { update({ enabled: v }); if (v) playUiSound(undefined, true); }} aria-label={en ? "Enable sounds" : "Activar sonidos"} />
        </div>

        <p className="mt-4 mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{en ? "Try and choose" : "Prueba y elige"}</p>
        <div className="grid grid-cols-2 gap-2">
          {PROFILES.map((p) => {
            const active = prefs.profile === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => { update({ profile: p.key }); playUiSound(p.key, true); }}
                className={`min-h-[44px] rounded-lg border px-3 py-2 text-left transition-colors ${active ? "border-foreground bg-radar-yellow text-radar-ink" : "border-border bg-card text-foreground hover:bg-muted"}`}
              >
                <span className="block text-sm font-medium">{en ? p.en : p.es}</span>
                <span className={`block text-[11px] ${active ? "text-radar-ink/70" : "text-muted-foreground"}`}>{en ? p.hintEn : p.hintEs}</span>
              </button>
            );
          })}
        </div>

        <p className="mt-4 mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{en ? "Volume" : "Volumen"}</p>
        <div className="grid grid-cols-3 gap-2">
          {VOLUMES.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => { update({ volume: v.key }); playUiSound(undefined, true); }}
              className={`min-h-[44px] rounded-lg border text-sm transition-colors ${prefs.volume === v.key ? "border-foreground bg-foreground text-background" : "border-border bg-card text-foreground hover:bg-muted"}`}
            >
              {en ? v.en : v.es}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
